import AuthService from './AuthService';

class AdminAgentService {
    constructor() {
        const rawBaseUrl = process.env.REACT_APP_CHATBOT_API_URL || process.env.CHATBOT_API_URL || process.env.REACT_APP_API_URL || '';
        this.apiBaseUrl = String(rawBaseUrl).replace(/\/+$/, '');
    }

    getAuthHeaders(extraHeaders) {
        const token = AuthService.getAccessToken();
        const authHeader = token ? { Authorization: `Bearer ${token}` } : {};
        return {
            ...authHeader,
            ...(extraHeaders || {}),
        };
    }

    sanitizeCreatePayload(payload) {
        const body = { ...(payload || {}) };
        delete body.agentId;
        delete body.createdAt;
        return body;
    }

    sanitizeUpdatePayload(agentId, payload) {
        const body = { ...(payload || {}) };
        body.agentId = agentId;
        delete body.createdAt;
        return body;
    }

    agentsBaseUrl(clientId) {
        if (!clientId) throw new Error('clientId is required');
        return `${this.apiBaseUrl}/admin/clients/${encodeURIComponent(clientId)}/agents`;
    }

    async getAgents(clientId) {
        const url = this.agentsBaseUrl(clientId);
        const response = await fetch(url, {
            method: 'GET',
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return Array.isArray(data) ? data : [];
    }

    async getAgent(clientId, agentId) {
        if (!agentId) throw new Error('agentId is required');

        const url = `${this.agentsBaseUrl(clientId)}/${encodeURIComponent(agentId)}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        return await response.json();
    }

    async createAgent(clientId, payload) {
        const url = this.agentsBaseUrl(clientId);
        const body = this.sanitizeCreatePayload(payload);

        const response = await fetch(url, {
            method: 'POST',
            headers: this.getAuthHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(body),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        return await response.json();
    }

    async updateAgent(clientId, agentId, payload) {
        if (!agentId) throw new Error('agentId is required');

        const url = `${this.agentsBaseUrl(clientId)}/${encodeURIComponent(agentId)}`;
        const body = this.sanitizeUpdatePayload(agentId, payload);

        const response = await fetch(url, {
            method: 'PUT',
            headers: this.getAuthHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(body),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        return await response.json();
    }

    async discoverMcpTools(clientId, agentId, serverId) {
        const url = `${this.agentsBaseUrl(clientId)}/${encodeURIComponent(agentId)}/mcp/servers/${encodeURIComponent(serverId)}/tools`;
        const response = await fetch(url, { method: 'GET', headers: this.getAuthHeaders() });
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        const data = await response.json();
        return data && typeof data === 'object' ? data : { tools: [], detectedTransport: null };
    }

    async deleteMcpServer(clientId, agentId, serverId) {
        const url = `${this.agentsBaseUrl(clientId)}/${encodeURIComponent(agentId)}/mcp/servers/${encodeURIComponent(serverId)}`;
        const response = await fetch(url, { method: 'DELETE', headers: this.getAuthHeaders() });
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    }

    async deleteAgent(clientId, agentId) {
        if (!agentId) throw new Error('agentId is required');

        const url = `${this.agentsBaseUrl(clientId)}/${encodeURIComponent(agentId)}`;
        const response = await fetch(url, {
            method: 'DELETE',
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const text = await response.text();
        try {
            return text ? JSON.parse(text) : { ok: true };
        } catch {
            return { ok: true };
        }
    }
}

const adminAgentService = new AdminAgentService();
export { AdminAgentService };
export default adminAgentService;
